import { useRef } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { CSSTransition, SwitchTransition } from 'react-transition-group'

import Button from '@/components/Button/Button'
import Conversation from '@/components/Chat/Conversation'
import Message from '@/components/Chat/Message'
import { useAppState } from '@/composables/useAppState'
import { useGSAP } from '@/composables/useGSAP'
import { useTransComponents } from '@/composables/useTransComponents'
import { UIAudio } from '@/config/AudioConfig'
import AudioEngine from '@/webgl/audio/AudioEngine'

export type ConversationChoiceType = {
  i18nKey: string
  ariaLabelI18nKey: string
  value: string
}

export type QuestionsBinaryProps = {
  index: number
  max: number
  className?: string
  appearProfile?: boolean
  introduction?: string
  question: string
  choices?: ConversationChoiceType[]
  onChoose?: (choice: string) => void
}

export default function QuestionsBinary({
  index,
  max,
  className,
  introduction,
  question,
  appearProfile,
  choices,
  onChoose,
}: QuestionsBinaryProps) {
  const { persona } = useAppState()
  const { components } = useTransComponents()
  const { t } = useTranslation()
  const { gsap } = useGSAP()

  const hasIntroduction = index === 1 && !!introduction

  // Refs required by react-transition-group
  const introRef = useRef<HTMLDivElement>(null)
  const questionRef = useRef<HTMLDivElement>(null)
  const buttonsRef = useRef<HTMLDivElement>(null)

  // GSAP timeline helpers
  const createEnterTimeline = (node: HTMLElement, delay = 0) => {
    const tl = gsap.timeline({ delay })

    tl.fromTo(node, { opacity: 0, x: 100 }, { opacity: 1, x: 0, duration: 0.3, ease: 'power2.out' })

    return tl
  }

  const createExitTimeline = (node: HTMLElement) => {
    const tl = gsap.timeline()

    // Start message exit at the same time
    tl.to(node, { opacity: 0, x: -50, duration: 0.3, ease: 'power2.out' }, 0)

    return tl
  }

  return (
    <Conversation
      persona={persona}
      className={`QuestionsBinary pt-[240px] md:pt-[15%] max-md:pb-[53.333vw] relative z-2 ${className ? className : ''}`}
      appearProfile={appearProfile}
      ariaHeading={t('questions.aria.binary.heading', { step: index, max })}
    >
      <>
        <CSSTransition
          in={hasIntroduction}
          appear
          timeout={200}
          mountOnEnter
          unmountOnExit
          nodeRef={introRef}
          onEnter={() => {
            if (introRef.current) {
              createEnterTimeline(introRef.current, 0.3)
            }
          }}
          onExit={() => {
            if (introRef.current) {
              createExitTimeline(introRef.current)
            }
          }}
        >
          <Message
            ref={introRef}
            className='mb-8 ml-48 md:ml-56 md:mr-32 col-start-1 md:col-start-1 col-span-10 md:col-span-6 mt-20'
          >
            <Trans i18nKey={introduction!} components={components} />
          </Message>
        </CSSTransition>

        <SwitchTransition mode='out-in'>
          <CSSTransition
            key={`q_${index}`}
            appear
            timeout={200}
            mountOnEnter
            unmountOnExit
            nodeRef={questionRef}
            onEnter={() => {
              if (questionRef.current) {
                createEnterTimeline(questionRef.current, hasIntroduction ? 2 : 0.3)
              }
            }}
            onExit={() => {
              if (questionRef.current) {
                createExitTimeline(questionRef.current)
              }
            }}
          >
            <Message
              ref={questionRef}
              className={`mb-8 ml-48 md:ml-56 md:mr-32 col-start-1 md:col-start-1 col-span-10 md:col-span-6 ${hasIntroduction ? '' : 'mt-20'}`}
            >
              <Trans i18nKey={question} components={components} />
            </Message>
          </CSSTransition>
        </SwitchTransition>

        {!!choices?.length && (
          <div ref={buttonsRef}>
            <div className='mb-8 ml-48 md:ml-56 md:mr-32 col-start-1 md:col-start-1 col-span-10 md:col-span-6 mt-[20px] flex'>
              {choices.map((choice, index) => (
                <Button
                  key={question + '_' + index}
                  is='button'
                  className='mr-[32px]'
                  appear
                  appearDelay={(index + 1) * 0.2 + (hasIntroduction ? 2 : 0.3)}
                  onClick={() => {
                    AudioEngine.play(UIAudio.ClickAlt)
                    onChoose?.(choice.value)
                  }}
                  aria-label={t(choice.ariaLabelI18nKey)}
                >
                  <Trans i18nKey={choice.i18nKey} components={components} />
                </Button>
              ))}
            </div>
          </div>
        )}
      </>
    </Conversation>
  )
}
